import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";

// Searchable single-select with an explicit "Add" action for names not in
// the list. Free text never commits: closing the list reverts the input to
// the selected value, and matching is case-insensitive, so near-duplicates
// like "tanaka" next to "Tanaka" cannot creep into the data.

export function Combobox({
  value,
  options,
  onChange,
  onCreate,
  placeholder,
  addLabel = "Add",
  autoFocus = false,
}: {
  value: string | null;
  options: string[];
  onChange: (value: string | null) => void;
  /** Called for a searched name that matches no option. Returns the
   *  canonical spelling to select (an existing case-insensitive match on
   *  the server wins over what was typed). */
  onCreate: (name: string) => Promise<string>;
  placeholder?: string;
  addLabel?: string;
  /** Focus (and open) the list on mount — for inline edit-in-place uses. */
  autoFocus?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  const exact = options.find((o) => o.toLowerCase() === q);
  const canAdd = q.length > 0 && !exact;
  const rowCount = filtered.length + (canAdd ? 1 : 0);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const openList = () => {
    setQuery("");
    setActive(0);
    setOpen(true);
  };

  const pick = (name: string) => {
    onChange(name);
    setOpen(false);
  };

  const add = async () => {
    const name = query.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      pick(await onCreate(name));
    } catch {
      // Leave the list open so the user can retry.
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, rowCount - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active < filtered.length) {
        const name = filtered[active];
        if (name) pick(name);
      } else if (canAdd) {
        void add();
      }
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          className={cn(
            "h-9 w-full px-3.5 pr-14 text-[14px] text-text placeholder:text-text-3",
            "rounded-md border border-border bg-surface",
            "focus:outline-2 focus:outline-brand-500 focus:outline focus:outline-offset-1",
          )}
          placeholder={placeholder}
          autoFocus={autoFocus}
          value={open ? query : (value ?? "")}
          onFocus={openList}
          onChange={(e) => {
            if (!open) setOpen(true);
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
        <span className="absolute inset-y-0 right-2 flex items-center gap-0.5">
          {value && !open && (
            <button
              type="button"
              aria-label="Clear"
              className="rounded p-1 text-text-3 hover:text-text"
              onClick={() => onChange(null)}
            >
              <X size={13} aria-hidden />
            </button>
          )}
          <button
            type="button"
            aria-label="Open list"
            tabIndex={-1}
            className="rounded p-1 text-text-3 hover:text-text"
            onClick={() => {
              if (open) {
                setOpen(false);
              } else {
                openList();
                inputRef.current?.focus();
              }
            }}
          >
            <ChevronDown size={14} aria-hidden />
          </button>
        </span>
      </div>

      {open && (
        <div
          role="listbox"
          className="absolute z-40 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-surface py-1 shadow-pop"
        >
          {filtered.map((name, i) => (
            <button
              key={name}
              type="button"
              role="option"
              aria-selected={name === value}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[14px] text-text",
                i === active ? "bg-surface-2" : "hover:bg-surface-2",
              )}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(name)}
            >
              <span className="w-4 shrink-0">
                {name === value && <Check size={14} className="text-brand-on" aria-hidden />}
              </span>
              <span className="truncate">{name}</span>
            </button>
          ))}
          {canAdd && (
            <button
              type="button"
              disabled={busy}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[14px] font-semibold text-brand-on",
                active === filtered.length ? "bg-surface-2" : "hover:bg-surface-2",
                busy && "opacity-60",
              )}
              onMouseEnter={() => setActive(filtered.length)}
              onClick={() => void add()}
            >
              <Plus size={14} className="w-4 shrink-0" aria-hidden />
              <span className="truncate">
                {addLabel} “{query.trim()}”
              </span>
            </button>
          )}
          {rowCount === 0 && (
            <p className="px-3 py-1.5 text-[13px] text-text-3">Type a name to add it.</p>
          )}
        </div>
      )}
    </div>
  );
}
